import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Bell,
  CheckCircle,
  Clock,
  FileText,
  Filter,
  CheckCheck,
  Trash2,
  ExternalLink,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationsCenterPage = () => {
  const navigate = useNavigate();
  
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetchNotifications();
  }, [filter]);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/notifications/list?unread_only=${filter === 'unread'}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      console.error('خطأ في جلب الإشعارات:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${BACKEND_URL}/api/notifications/${notificationId}/mark-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNotifications();
    } catch (err) {
      console.error('خطأ في تعليم الإشعار:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/notifications/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNotifications();
    } catch (err) {
      console.error('خطأ في تعليم الكل:', err);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/notifications/${notificationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNotifications();
    } catch (err) {
      console.error('خطأ في حذف الإشعار:', err);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'contract_signed':
      case 'contract_completed':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'contract_pending_signature':
        return <Clock className="w-6 h-6 text-yellow-600" />;
      case 'contract_updated':
        return <FileText className="w-6 h-6 text-blue-600" />;
      case 'system_alert':
        return <AlertCircle className="w-6 h-6 text-red-600" />;
      default:
        return <Bell className="w-6 h-6 text-gray-600" />;
    }
  };

  const getNotificationColor = (type, isRead) => {
    if (isRead) return 'bg-gray-50 border-gray-200';
    
    switch (type) {
      case 'contract_signed':
      case 'contract_completed':
        return 'bg-green-50 border-green-300';
      case 'contract_pending_signature':
        return 'bg-yellow-50 border-yellow-300';
      case 'contract_updated':
        return 'bg-blue-50 border-blue-300';
      case 'system_alert':
        return 'bg-red-50 border-red-300';
      default:
        return 'bg-white border-gray-200';
    }
  };

  const getPriorityBadge = (priority) => {
    const config = {
      urgent: { text: 'عاجل', color: 'bg-red-500 text-white' },
      high: { text: 'مهم', color: 'bg-orange-500 text-white' },
      medium: { text: 'متوسط', color: 'bg-blue-500 text-white' },
      low: { text: 'منخفض', color: 'bg-gray-400 text-white' }
    };
    
    const badge = config[priority] || config.medium;
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-bold ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'الآن';
    if (minutes < 60) return `منذ ${minutes} دقيقة`;
    if (hours < 24) return `منذ ${hours} ساعة`;
    if (days < 7) return `منذ ${days} يوم`;
    
    return date.toLocaleDateString('ar-SA');
  };

  const filteredNotifications = notifications.filter(notif => {
    if (filter === 'unread' && notif.is_read) return false;
    if (filter === 'read' && !notif.is_read) return false;
    if (typeFilter !== 'all' && notif.notification_type !== typeFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C8A870] mx-auto"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4" dir="rtl">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-[#C8A870] to-[#b8986a] p-3 rounded-lg relative">
                <Bell className="w-8 h-8 text-white" />
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1e3a8a]">مركز الإشعارات</h1>
                <p className="text-gray-600">{unreadCount} إشعار غير مقروء</p>
              </div>
            </div>
            
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-2 bg-gradient-to-r from-[#C8A870] to-[#b8986a] text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg"
              >
                <CheckCheck className="w-5 h-5" />
                تعليم الكل كمقروء
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <span className="font-bold text-gray-900">الفلاتر</span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filter === 'all'
                  ? 'bg-[#C8A870] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              الكل ({notifications.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filter === 'unread'
                  ? 'bg-[#C8A870] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              غير مقروء ({unreadCount})
            </button>
            <button
              onClick={() => setFilter('read')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filter === 'read'
                  ? 'bg-[#C8A870] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              مقروء ({notifications.length - unreadCount})
            </button>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white font-semibold"
            >
              <option value="all">جميع الأنواع</option>
              <option value="contract_signed">توقيع عقد</option>
              <option value="contract_pending_signature">بانتظار التوقيع</option>
              <option value="contract_completed">عقد مكتمل</option>
              <option value="contract_updated">تحديث عقد</option>
            </select>
          </div>
        </div>

        {/* Notifications List */}
        <div className="space-y-4">
          {filteredNotifications.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
              <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 text-lg">لا توجد إشعارات</p>
            </div>
          ) : (
            filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`border-2 rounded-xl p-5 transition-all cursor-pointer hover:shadow-lg ${getNotificationColor(notification.notification_type, notification.is_read)}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    {getNotificationIcon(notification.notification_type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`font-bold text-gray-900 ${!notification.is_read ? 'text-lg' : ''}`}>
                            {notification.title_ar}
                          </h3>
                          {!notification.is_read && (
                            <span className="w-2 h-2 bg-[#C8A870] rounded-full"></span>
                          )}
                        </div>
                        <p className="text-gray-600 text-sm">
                          {notification.message_ar}
                        </p>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2 mr-4">
                        {getPriorityBadge(notification.priority)}
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {formatDate(notification.created_at)}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-3">
                      {notification.action_url && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNotificationClick(notification);
                          }}
                          className="flex items-center gap-1 text-sm text-[#C8A870] hover:text-[#b8986a] font-semibold"
                        >
                          <ExternalLink className="w-4 h-4" />
                          الانتقال
                        </button>
                      )}
                      
                      {!notification.is_read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                          }}
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-semibold"
                        >
                          <CheckCircle className="w-4 h-4" />
                          تعليم كمقروء
                        </button>
                      )}
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                        className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 font-semibold mr-auto"
                      >
                        <Trash2 className="w-4 h-4" />
                        حذف
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsCenterPage;
